<script lang="ts" generics="TValues">
    import Row from '$components/Form/Row.svelte';
    import type { Form } from '@jmvdevelop/svelte-form';
    import type { Fields } from '$components/MultiQuoteForm/MultiQuoteFormData';
    import Select from '$components/Form/Select.svelte';
    import MeltMultiSelect from '$components/Form/MeltMultiSelect.svelte';
    import PrettyChoice from '$components/Form/PrettyChoice.svelte';
    import Input from '$components/Form/Input.svelte';
    import Textarea from '$components/Form/Textarea.svelte';

    export let form: Form<TValues, string>;
    export let fields: Fields<TValues>;

    const specificRequirementsOptions = [
        'Assurance marchandise',
        'Chargement / déchargement',
        'Dédouanement complet',
        'Gestion documentaire',
        'Livraison urgente',
        'Manutention spécialisée',
        'Service porte-à-porte',
        'Stockage intermédiaire',
        'Suivi en temps réel',
        'Transport sécurisé',
    ].map((v) => ({ label: v, value: v }));
</script>

<div class="flex flex-col gap-8 @container">
    <Row
        {form}
        field={fields.transit.transitType}
        class=""
        let:field
        let:id
        label={`Quel type de transit recherchez-vous ?`}
    >
        <PrettyChoice
            {form}
            {field}
            {id}
            defaultClass="grid grid-cols-1 @[315px]:grid-cols-3 gap-x-6 gap-y-0"
            choices={['Maritime', 'Aérien', 'Terrestre'].map((v) => ({
                label: v,
                value: v,
                key: v,
            }))}
        />
    </Row>

    <Row
        {form}
        field={fields.transit.isGoodsAvailable}
        class=""
        let:field
        let:id
        label={`Votre marchandise est-elle déjà disponible ?`}
    >
        <Select
            {form}
            {id}
            {field}
            required={false}
            showChevron
            options={['Oui', `Non, en cours d’acheminement`, `Non, en attente`].map(
                (v) => ({
                    label: v,
                    value: v,
                    key: v,
                }),
            )}
        />
    </Row>

    <Row
        {form}
        field={fields.transit.goodsStartPoint}
        class=""
        let:field
        let:id
        label={`Quel est le point de départ de votre marchandise ?`}
    >
        <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
    </Row>

    <Row
        {form}
        field={fields.transit.goodsFinalDestination}
        class=""
        let:field
        let:id
        label={`Quelle est la destination finale ?`}
    >
        <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
    </Row>

    <Row
        {form}
        field={fields.transit.clearanceServiceRequired}
        class=""
        let:field
        let:id
        label={`Avez-vous besoin d’un service de dédouanement ?`}
    >
        <PrettyChoice
            {form}
            {field}
            {id}
            defaultClass="grid grid-cols-1 @[300px]:grid-cols-2 @[370px]:grid-cols-3 gap-x-6 gap-y-0"
            choices={['Oui', `Non`, `Je ne sais pas`].map((v) => ({
                label: v,
                value: v,
                key: v,
            }))}
        />
    </Row>

    <Row
        {form}
        field={fields.transit.temporaryStorageRequired}
        class=""
        let:field
        let:id
        label={`Souhaitez-vous un service d’entreposage temporaire ?`}
    >
        <PrettyChoice
            {form}
            {field}
            {id}
            defaultClass="grid grid-cols-1 @[300px]:grid-cols-2 @[370px]:grid-cols-3 gap-x-6 gap-y-0"
            choices={['Oui', `Non`, `Peut-être, selon les tarifs`].map((v) => ({
                label: v,
                value: v,
                key: v,
            }))}
        />
    </Row>

    <Row
        {form}
        field={fields.transit.goodsType}
        class=""
        let:field
        let:id
        label={`Quel est le type de marchandise ?`}
    >
        <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
    </Row>

    <div class="flex flex-col @[460px]:flex-row gap-8">
        <Row
            {form}
            field={fields.transit.goodsVolume}
            class="flex-1"
            let:field
            let:id
            label={`Quel est le volume estimé de la marchandise ?`}
        >
            <div class="flex gap-2">
                <Input
                    {form}
                    {field}
                    {id}
                    toText={(v) => v}
                    fromText={(v) => v}
                    placeholder="Volume estimé en m³"
                />
                <div
                    class="control-form bg-gray-200 text-gray-700 readonly basis-[content] italic"
                >
                    m³
                </div>
            </div>
        </Row>

        <Row
            {form}
            field={fields.transit.goodsWeight}
            class="flex-1"
            let:field
            let:id
            label={`Quel est le poids estimé de la marchandise ?`}
        >
            <div class="flex gap-2">
                <Input
                    {form}
                    {field}
                    {id}
                    toText={(v) => v}
                    fromText={(v) => v}
                    placeholder="Poids estimé en kg"
                />
                <div
                    class="control-form bg-gray-200 text-gray-700 readonly basis-[content] italic"
                >
                    kg
                </div>
            </div>
        </Row>
    </div>

    <Row
        {form}
        field={fields.transit.specificRequirements}
        class=""
        let:field
        let:id
        label={`Avez-vous des exigences particulières ?`}
    >
        <MeltMultiSelect
            {form}
            {id}
            {field}
            placeholder=""
            options={specificRequirementsOptions}
            valueToOption={(v) =>
                specificRequirementsOptions.find((o) => o.value === v) ?? {
                    value: v,
                    label: v,
                }}
        />
    </Row>

    <Row
        {form}
        field={fields.transit.transitDate}
        class=""
        let:field
        let:id
        label={`Quand souhaitez-vous organiser le transit ?`}
    >
        <PrettyChoice
            {form}
            {field}
            {id}
            defaultClass="grid grid-cols-1 @[280px]:grid-cols-2 gap-x-6 gap-y-0"
            choices={[
                'Dès que possible',
                'Dans les 7 jours',
                'Dans le mois',
                'À une date précise (à renseigner dans votre message)',
            ].map((v) => ({ label: v, value: v, key: v }))}
        />
    </Row>

    <Row
        {form}
        field={fields.message}
        required
        label={'Votre demande'}
        helpLabel="Précisez votre besoin, soyez le plus précis dans votre demande de devis"
        let:field
    >
        <Textarea
            {form}
            {field}
            placeholder={`Précisez votre besoin, soyez le plus précis dans votre demande de devis`}
            toText={(id) => id}
            fromText={(id) => id}
            class={''}
            rows={5}
        />
    </Row>
</div>
